import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

//components
import StyledTable from '../../components/ReactTable/StyledTable'
import StyledTableContainer from '../../components/StyledTableContainer/StyledTableContainer'
import StyledEmptyHeader from '../../components/StyledEmptyHeader/StyledEmptyHeader'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'

//utils
import toolService from '../../service/toolLife.service'
import { OptionItem } from '../../types/common'
import { ToolsApiDataWithIndex } from '../../types/tools'
import { MACHINES } from '../../utils/constant.utils'

const machineOptions = MACHINES.map((machine) => ({
  value: machine.value,
  label: machine.label
}))

type ToolLIfeSummaryType = ToolsApiDataWithIndex & {
  partCount: number
  remaining: number
}

const ToolLifeSummary = () => {
  const defaultMachine = {
    value: 'all',
    label: 'All machines'
  }
  const defaultToolIdNo = {
    value: 'all',
    label: 'All tool id'
  }
  const defaultToolType = {
    value: 'all',
    label: 'All tool type'
  }
  const defaultToolSize = {
    value: 'all',
    label: 'All tool size'
  }

  const [tools, setTools] = React.useState<Record<
    number,
    ToolLIfeSummaryType
  > | null>(null)
  const [toolIdOptions, setToolIdOption] = React.useState<OptionItem[]>([])
  const [toolTypeOptions, setToolTypeOption] = React.useState<OptionItem[]>([])
  const [toolSizeOptions, setToolSizeOption] = React.useState<OptionItem[]>([])

  const [toolId, setToolId] = React.useState<OptionItem>(defaultToolIdNo)
  const [toolType, setToolType] = React.useState<OptionItem>(defaultToolType)
  const [toolSize, setToolSize] = React.useState<OptionItem>(defaultToolSize)
  const [machine, setMachine] = React.useState<OptionItem>(defaultMachine)
  const [isLoading, setIsLoading] = React.useState(false)

  const fetchTools = async () => {
    setIsLoading(true)
    const toolsResponse = await toolService.getTools({})
    const toolsUsageResponse = await toolService.getToolsUsage()

    if (toolsResponse.success && toolsUsageResponse.success) {
      const toolsData: Record<string, ToolLIfeSummaryType> = {}
      const toolIdNoOptionsObj: Record<string, OptionItem> = {}
      const toolTypeOptionsObj: Record<string, OptionItem> = {}
      const toolSizeOptionsObj: Record<string, OptionItem> = {}

      let count = 0
      for (const tool of toolsResponse.data) {
        const found = toolsUsageResponse.data.find(
          (item) => item.toolId.trim() === tool.toolId
        )

        if (!found) {
          continue
        }
        count = count + 1
        if (!toolIdNoOptionsObj[tool.toolId]) {
          toolIdNoOptionsObj[tool.toolId] = {
            label: tool.toolId,
            value: tool.toolId
          }
        }

        if (!toolTypeOptionsObj[tool.toolTypeId]) {
          toolTypeOptionsObj[tool.toolTypeId] = {
            label: tool.toolTypeId,
            value: tool.toolTypeId
          }
        }
        if (!toolSizeOptionsObj[tool.toolSize]) {
          toolSizeOptionsObj[tool.toolSize] = {
            label: tool.toolSize,
            value: tool.toolSize
          }
        }
        if (!toolsData[tool.toolId]) {
          toolsData[tool.toolId] = {
            index: count,
            ...tool,
            partCount: found.partCount,
            remaining: tool.toolLife - found.partCount
          }
        }
      }

      setToolIdOption(Object.values(toolIdNoOptionsObj))
      setToolTypeOption(Object.values(toolTypeOptionsObj))
      setToolSizeOption(Object.values(toolSizeOptionsObj))
      setTools(toolsData)
    } else {
      //TODO: set error or show error
      setTools(null)
    }

    setIsLoading(false)
  }

  React.useEffect(() => {
    fetchTools()
  }, [])

  //1. Create search object
  const searchObj = {
    ...(toolId &&
      toolId.value !== 'all' && {
        toolId: toolId.value
      }),
    ...(toolType &&
      toolType.value !== 'all' && {
        toolTypeId: toolType.value
      }),
    ...(toolSize &&
      toolSize.value !== 'all' && {
        toolSize: toolSize.value
      }),
    ...(machine &&
      machine.value !== 'all' && {
        machineId: machine.value
      })
  }

  //2. Extract the keys into an array
  const keysToChecked = Object.keys(searchObj) as (keyof typeof searchObj)[]

  //3. filter
  const filteredRows = React.useMemo(() => {
    let filteredRows = tools ? Object.values(tools) : []
    if (tools && keysToChecked.length > 0) {
      filteredRows = Object.values(tools).filter((toolRow) => {
        return keysToChecked.every((key) => searchObj[key] === toolRow[key])
      })
    }

    return filteredRows
  }, [tools, toolType, toolId, toolSize])

  const indianFormatter = new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2
  })

  const toolColumns = [
    {
      Header: 'S.No',
      accessor: 'index'
    },
    {
      Header: 'Id',
      accessor: 'id'
    },
    {
      Header: 'Tool ID',
      accessor: 'toolId'
    },
    {
      Header: 'Tool type',
      accessor: 'toolType',
      Cell: ({
        cell: { row }
      }: {
        cell: { row: { original: ToolLIfeSummaryType } }
      }) => {
        return <>{row.original.toolType.type}</>
      }
    },
    {
      Header: 'Tool size',
      accessor: 'toolSize'
    },
    {
      Header: 'Machine',
      accessor: 'machineId',
      Cell: ({
        cell: { row }
      }: {
        cell: { row: { original: ToolLIfeSummaryType } }
      }) => {
        return <>{row.original.machine.displayName}</>
      }
    },
    {
      Header: 'Tool life',
      accessor: 'toolLife',
      Cell: ({
        cell: { row }
      }: {
        cell: { row: { original: ToolLIfeSummaryType } }
      }) => {
        return <>{indianFormatter.format(row.original.toolLife)}</>
      }
    },
    {
      Header: 'Consumed life',
      accessor: 'partCount',
      Cell: ({
        cell: { row }
      }: {
        cell: { row: { original: ToolLIfeSummaryType } }
      }) => {
        return <>{indianFormatter.format(row.original.partCount)}</>
      }
    },
    {
      Header: 'Remaining life',
      accessor: 'remaining',
      Cell: ({
        cell: { row }
      }: {
        cell: { row: { original: ToolLIfeSummaryType } }
      }) => {
        return <>{indianFormatter.format(row.original.remaining)}</>
      }
    }
  ]

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'column',
          gap: '12px',
          borderBottom: '1px solid #eeeeee',
          backgroundColor: '#fff',
          padding: '12px 16px',
          zIndex: 100
        }}
      >
        <Stack
          spacing={2}
          direction="row"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          <StyledSelectField
            label=""
            placeHolderText="Select tool id"
            selectedValue={toolId}
            selectOptions={[defaultToolIdNo, ...toolIdOptions]}
            onChangeHandler={(value: OptionItem) => setToolId(value)}
            isMulti={false}
            isSearchable={true}
            isDisabled={false}
            noOptionsMessage="No tool id found"
          />
          <StyledSelectField
            label=""
            placeHolderText="Select tool type"
            selectedValue={toolType}
            selectOptions={[defaultToolType, ...toolTypeOptions]}
            onChangeHandler={(value: OptionItem) => setToolType(value)}
            isMulti={false}
            isSearchable={true}
            isDisabled={false}
            noOptionsMessage="No tool type found"
          />

          <StyledSelectField
            label=""
            placeHolderText="Select tool size"
            selectedValue={toolSize}
            selectOptions={[defaultToolSize, ...toolSizeOptions]}
            onChangeHandler={(value: OptionItem) => setToolSize(value)}
            isMulti={false}
            isSearchable={true}
            isDisabled={false}
            noOptionsMessage="No tool Size found"
          />
          <StyledSelectField
            label=""
            placeHolderText="Select machine"
            selectedValue={machine}
            selectOptions={[defaultMachine, ...machineOptions]}
            onChangeHandler={(value: OptionItem) => setMachine(value)}
            isMulti={false}
            isSearchable={true}
            isDisabled={false}
            noOptionsMessage="No machine found"
          />
        </Stack>
      </Stack>
      <Box sx={{ padding: '16px' }}>
        <StyledTableContainer>
          {isLoading && <StyledEmptyHeader>Loading...</StyledEmptyHeader>}
          {!isLoading && filteredRows.length === 0 && (
            <StyledEmptyHeader>There are no tools</StyledEmptyHeader>
          )}
          {!isLoading && filteredRows.length > 0 && (
            <StyledTable
              pagination={false}
              columns={toolColumns}
              data={filteredRows}
              hiddenColumns={['id', 'index']}
            />
          )}
        </StyledTableContainer>
      </Box>
    </>
  )
}

export default ToolLifeSummary
